import React, { useEffect, useRef, useState } from "react";
import { View, TouchableOpacity } from "react-native";
import { Controller } from "react-hook-form";
import { Feather } from "@expo/vector-icons";
import type { ActionSheetRef } from "react-native-actions-sheet";
import { Modal } from "@/components/ui/Modal";
import { AppTextInput } from "@/components/ui/TextInput";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { useI18n } from "@/context/I18nContext";
import { useIsWideScreen } from "@/hooks/useIsWideScreen";
import { useProfileChangePasswordForm } from "@/features/auth/hooks/useProfileChangePasswordForm";
import { AppActionSheet } from "@/components/sheets/ActionSheet";
import { toast } from "@/lib/toast";
import type { TranslationKeys } from "@chinese-vendor/i18n";

interface ChangePasswordModalProps {
  visible: boolean;
  onDismiss: () => void;
}

export function ChangePasswordModal({ visible, onDismiss }: ChangePasswordModalProps) {
  const { t } = useI18n();
  const isWide = useIsWideScreen();
  const { form, isLoading, error, handleSubmit, reset } = useProfileChangePasswordForm();
  const actionSheetRef = useRef<ActionSheetRef>(null);

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Show/hide action sheet on narrow screens when visibility changes
  useEffect(() => {
    if (isWide) return;
    if (visible) {
      actionSheetRef.current?.show();
    } else {
      actionSheetRef.current?.hide();
    }
  }, [visible, isWide]);

  // Show API errors as toast
  useEffect(() => {
    if (!error) return;
    toast.error({ message: t(error as TranslationKeys) });
  }, [error, t]);

  const handleDismiss = () => {
    if (isLoading) return;
    reset();
    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
    onDismiss();
  };

  const { current_password, new_password, confirm_new_password } = form.watch();
  const allFilled =
    current_password.length > 0 && new_password.length > 0 && confirm_new_password.length > 0;

  const formFields = (fieldPadding: boolean) => (
    <View className={`gap-1 ${fieldPadding ? "px-4" : ""}`}>
      {/* Old / Current Password */}
      <Controller
        control={form.control}
        name="current_password"
        render={({ field: { value, onChange, onBlur }, fieldState: { error: fieldError } }) => (
          <AppTextInput
            required
            label={
              fieldPadding
                ? "profile.changePassword.oldPasswordLabel"
                : "auth.changePassword.currentPasswordLabel"
            }
            placeholder={
              fieldPadding
                ? "profile.changePassword.oldPasswordPlaceholder"
                : "auth.changePassword.currentPasswordPlaceholder"
            }
            value={value}
            onChangeText={onChange}
            onBlur={onBlur}
            secureTextEntry={!showCurrent}
            error={fieldError?.message as TranslationKeys | undefined}
            testID="change-password-current-input"
            trailingIcon={
              <TouchableOpacity
                onPress={() => setShowCurrent((v) => !v)}
                accessibilityRole="button"
              >
                <Icon
                  as={Feather}
                  name={showCurrent ? "eye-off" : "eye"}
                  size={20}
                  className="text-supporting-text"
                />
              </TouchableOpacity>
            }
          />
        )}
      />

      {/* New Password */}
      <Controller
        control={form.control}
        name="new_password"
        render={({ field: { value, onChange, onBlur }, fieldState: { error: fieldError } }) => (
          <AppTextInput
            required
            label="auth.changePassword.newPasswordLabel"
            placeholder="auth.changePassword.newPasswordPlaceholder"
            value={value}
            onChangeText={onChange}
            onBlur={onBlur}
            secureTextEntry={!showNew}
            error={fieldError?.message as TranslationKeys | undefined}
            testID="change-password-new-input"
            trailingIcon={
              <TouchableOpacity onPress={() => setShowNew((v) => !v)} accessibilityRole="button">
                <Icon
                  as={Feather}
                  name={showNew ? "eye-off" : "eye"}
                  size={20}
                  className="text-supporting-text"
                />
              </TouchableOpacity>
            }
          />
        )}
      />

      {/* Confirm New Password */}
      <Controller
        control={form.control}
        name="confirm_new_password"
        render={({ field: { value, onChange, onBlur }, fieldState: { error: fieldError } }) => (
          <AppTextInput
            required
            label="auth.changePassword.confirmPasswordLabel"
            placeholder="auth.changePassword.confirmPasswordPlaceholder"
            value={value}
            onChangeText={onChange}
            onBlur={onBlur}
            secureTextEntry={!showConfirm}
            error={fieldError?.message as TranslationKeys | undefined}
            testID="change-password-confirm-input"
            trailingIcon={
              <TouchableOpacity
                onPress={() => setShowConfirm((v) => !v)}
                accessibilityRole="button"
              >
                <Icon
                  as={Feather}
                  name={showConfirm ? "eye-off" : "eye"}
                  size={20}
                  className="text-supporting-text"
                />
              </TouchableOpacity>
            }
          />
        )}
      />
    </View>
  );

  // ── Wide: centred modal (Cancel + Change Password) ─────────────────────────
  if (isWide) {
    return (
      <Modal
        visible={visible}
        onDismiss={handleDismiss}
        title="auth.changePassword.title"
        closeOnBackdrop={!isLoading}
        testID="change-password-modal"
        useScrollView={false}
        footer={
          <View className="flex-row gap-3 justify-end">
            <Button
              label="common.cancel"
              onPress={handleDismiss}
              variant="tertiary"
              disabled={isLoading}
              testID="change-password-cancel-btn"
            />
            <Button
              label="auth.changePassword.submit"
              onPress={handleSubmit}
              variant="primary"
              loading={isLoading}
              disabled={!allFilled || isLoading}
              testID="change-password-submit-btn"
            />
          </View>
        }
      >
        {formFields(false)}
      </Modal>
    );
  }

  // ── Narrow: bottom action sheet (full-width Reset Password button) ──────────
  return (
    <AppActionSheet
      ref={actionSheetRef}
      headerTitle="auth.changePassword.title"
      onClose={handleDismiss}
      closeOnTouchBackdrop={!isLoading}
      testID="change-password-sheet"
      footer={
        <View className="px-4 pb-8 pt-4">
          <Button
            label="profile.changePassword.submit"
            onPress={handleSubmit}
            variant="primary"
            loading={isLoading}
            disabled={!allFilled || isLoading}
            className="w-full"
            testID="change-password-submit-btn"
          />
        </View>
      }
    >
      <View className="pt-2 pb-4">{formFields(true)}</View>
    </AppActionSheet>
  );
}
